import {
  put,
  all,
  call,
  takeLatest,
  takeLeading,
  select,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);

const toDate = (snap, data, key) =>
  data[key] ? toDateFirebase(snap, data, key).toDate() : null;

const conversationTransformer = (snap) => {
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    startedAt: toDate(snap, data, 'startedAt'),
    endedAt: toDate(snap, data, 'endedAt'),
    stoppedAt: toDate(snap, data, 'stoppedAt'),
    handoffAt: toDate(snap, data, 'handoffAt'),
    lastLeadMessageAt: toDate(snap, data, 'lastLeadMessageAt'),
  };
};

function* fetchAiConversationsByLeadSaga({ lead }) {
  try {
    // No orderBy on purpose: paired with the filter on leadId it would want
    // a composite index, and a lead has a handful of conversations at most.
    // Sorting them here costs nothing and keeps the feature free of an index
    // to deploy.
    const conversationsRef = query(
      collection(db, 'aiConversations'),
      where('leadId', '==', lead.id),
    );
    const snap = yield call(rsf.firestore.getCollection, conversationsRef);

    const conversations = snap.docs
      .map(conversationTransformer)
      .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));

    yield put(actions.fetchAiConversationsByLeadSuccess(conversations));
  } catch (error) {
    yield put(actions.fetchAiConversationsByLeadFailure(error));
  }
}

function* fetchAiConversationMessagesSaga({ conversationId }) {
  try {
    const messagesRef = query(
      collection(db, 'aiConversations', conversationId, 'messages'),
      orderBy('at', 'asc'),
    );
    const snap = yield call(rsf.firestore.getCollection, messagesRef);

    yield put(
      actions.fetchAiConversationMessagesSuccess(
        snap.docs.map((doc) => {
          const data = doc.data();
          return { id: doc.id, ...data, at: toDate(doc, data, 'at') };
        }),
      ),
    );
  } catch (error) {
    yield put(actions.fetchAiConversationMessagesFailure(error));
  }
}

function* stopAiConversationSaga({ conversation }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);

    const stopAiConversations = httpsCallable(
      getFunctions(),
      'stopAiConversations-stopAiConversations',
    );
    const { data } = yield call(stopAiConversations, {
      companyId,
      conversationIds: [conversation.id],
    });

    if (data.error) throw new Error(data.error.message);

    yield put(actions.stopAiConversationSuccess());
    toastr.success(i18n.t('Lead back to the call center!'), '');

    // Read back instead of patching the row here: the callable decides what
    // it could actually stop, and a conversation an operator had already
    // taken is left alone.
    yield put(actions.fetchAiConversationsByLead({ id: conversation.leadId }));
  } catch (error) {
    yield put(actions.stopAiConversationFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* aiConversationSaga() {
  yield all([
    // takeLatest on the two reads: an operator opening a second lead while
    // the first read is still in flight would otherwise have that request
    // dropped, and see the first lead's conversations under the second
    // lead's name. The last one asked for is the one on screen.
    takeLatest(
      types.FETCH_AI_CONVERSATIONS_BY_LEAD.REQUEST,
      fetchAiConversationsByLeadSaga,
    ),
    takeLatest(
      types.FETCH_AI_CONVERSATION_MESSAGES.REQUEST,
      fetchAiConversationMessagesSaga,
    ),
    // The stop stays takeLeading: it writes, and a second press while the
    // first callable is running is a double click, not a change of mind.
    takeLeading(types.STOP_AI_CONVERSATION.REQUEST, stopAiConversationSaga),
  ]);
}

export default aiConversationSaga;
